import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Minus, Plus, Truck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { formatPrice } from "@/i18n";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cart, removeFromCart, updateQty, currency, t, lang, whatsappNumber } = useApp();
  const navigate = useNavigate();

  const total = cart.reduce((sum, p) => sum + p.price_rub * (p.quantity || 1), 0);

  const buildWhatsappLink = () => {
    const lines = cart.map(
      (p, i) => `${i + 1}. ${p.brand} — ${p.name}${p.size ? ` (${t.cart.size} ${p.size})` : ""} ×${p.quantity || 1} (${formatPrice(p.price_rub * (p.quantity || 1), currency)})`
    );
    const text = `${t.cart.msgIntro}\n\n${lines.join("\n")}\n\n${t.cart.total}: ${formatPrice(total, currency)}`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`;
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[70] bg-ink/40 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[440px] bg-paper flex flex-col border-l border-line"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-line">
              <h2 className="font-display text-3xl">{t.cart.title}</h2>
              <button onClick={() => setCartOpen(false)} data-testid="close-cart-button" aria-label="Close">
                <X className="h-6 w-6" strokeWidth={1.2} />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-10">
                <p className="font-display text-2xl mb-3">{t.cart.emptyTitle}</p>
                <p className="text-sm text-ink/50 font-light">{t.cart.emptySub}</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto divide-y divide-line" data-testid="cart-items">
                {cart.map((p) => (
                  <div key={p.key} className="flex gap-4 p-6" data-testid={`cart-item-${p.key}`}>
                    <img src={p.image} alt={p.name} className="h-24 w-20 object-cover bg-surface" />
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted">{p.brand}</p>
                      <h3 className="font-display text-xl leading-tight">{p.name}</h3>
                      {p.size ? (
                        <p className="text-[10px] uppercase tracking-[0.18em] text-ink/60 mt-1" data-testid={`cart-size-${p.key}`}>{t.cart.size}: {p.size}</p>
                      ) : (
                        <p className="text-xs text-ink/50 mt-1">{lang === "ru" ? p.subtitle_ru : p.subtitle_en}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-line" data-testid={`qty-control-${p.key}`}>
                          <button
                            onClick={() => updateQty(p.key, -1)}
                            disabled={(p.quantity || 1) <= 1}
                            data-testid={`qty-decrease-${p.key}`}
                            aria-label="Decrease quantity"
                            className="h-8 w-8 flex items-center justify-center hover:bg-surface disabled:opacity-30 transition-colors"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-8 text-center text-sm tabular-nums" data-testid={`qty-value-${p.key}`}>{p.quantity || 1}</span>
                          <button
                            onClick={() => updateQty(p.key, 1)}
                            disabled={(p.quantity || 1) >= (p.maxStock || 99)}
                            data-testid={`qty-increase-${p.key}`}
                            aria-label="Increase quantity"
                            className="h-8 w-8 flex items-center justify-center hover:bg-surface disabled:opacity-30 transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="text-sm" data-testid={`cart-line-total-${p.key}`}>{formatPrice(p.price_rub * (p.quantity || 1), currency)}</span>
                      </div>
                      <button
                        onClick={() => removeFromCart(p.key)}
                        data-testid={`remove-item-${p.key}`}
                        className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-ink transition-colors link-underline mt-2"
                      >
                        {t.cart.remove}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {cart.length > 0 && (
              <div className="border-t border-line p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-muted">{t.cart.total}</span>
                  <span className="font-display text-3xl" data-testid="cart-total">{formatPrice(total, currency)}</span>
                </div>
                <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-ink/50 mb-4" data-testid="cart-shipping-included">
                  <Truck className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.shop.shippingIncluded}
                </p>
                <button
                  onClick={() => { setCartOpen(false); navigate("/payment"); }}
                  data-testid="cart-payment-button"
                  className="group flex items-center justify-center gap-3 w-full bg-ink text-paper py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink"
                >
                  {t.payment.open} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href={buildWhatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="send-whatsapp-button"
                  className="flex items-center justify-center gap-2 w-full border border-ink py-3.5 mt-3 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-ink hover:text-paper"
                >
                  {t.cart.send}
                </a>
                <button
                  onClick={() => { setCartOpen(false); navigate("/contact"); }}
                  data-testid="cart-email-button"
                  className="flex items-center justify-center gap-2 w-full border border-ink py-3.5 mt-3 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-ink hover:text-paper"
                >
                  {t.inquiry.via}
                </button>
                <p className="text-[10px] text-muted text-center mt-3 font-light">{t.cart.note}</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
